import { useState } from "react"
import { requestMagicLink } from "@/lib/api"
import { Brand } from "@/components/Brand"

type State = { kind: "idle" } | { kind: "sending" } | { kind: "sent"; email: string } | { kind: "error"; msg: string }

/**
 * Read `?email=` and `?invite=` from the URL once, on first render. These
 * are seeded by the invitation message so a newly-invited user doesn't have
 * to retype either field. Read inline (lazy initial state) rather than in an
 * effect so the inputs render with the values already in place — no flash
 * of empty form.
 */
function initialFromURL(): { email: string; invite: string } {
  if (typeof window === "undefined") return { email: "", invite: "" }
  const p = new URLSearchParams(window.location.search)
  const rawCode = (p.get("invite") || p.get("code") || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
  return {
    email: (p.get("email") || "").trim().toLowerCase(),
    invite: rawCode,
  }
}

export function LoginPage() {
  const seed = initialFromURL()
  const [email, setEmail] = useState(seed.email)
  const [invite, setInvite] = useState(seed.invite)
  const [state, setState] = useState<State>({ kind: "idle" })
  const prefilled = Boolean(seed.email && seed.invite)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email) return
    setState({ kind: "sending" })
    try {
      await requestMagicLink(email.trim(), invite.trim() || undefined)
      setState({ kind: "sent", email })
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || "Something went wrong."
      setState({ kind: "error", msg })
    }
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.1fr_1fr]">
      {/* Left — editorial column */}
      <section className="bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 20%, hsl(var(--background)) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
          aria-hidden
        />
        <div className="relative h-full flex flex-col px-8 sm:px-14 py-10">
          <div className="flex items-baseline gap-3">
            <span
              className="font-display italic text-2xl text-moss leading-none"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 100' }}
            >
              f
            </span>
            <span className="eyebrow text-background/70">FitTrack · sign in</span>
          </div>

          <div className="flex-1 grid place-items-center py-16">
            <div className="max-w-md">
              <p className="eyebrow text-background/70 mb-6 animate-ink-in">private · invite-only</p>
              <h1
                className="font-display text-hero text-background tracking-tightest mb-8 animate-fade-up"
                style={{ animationDelay: "120ms" }}
              >
                The opposite of
                <br />
                <span className="italic text-moss">streak culture.</span>
              </h1>
              <p
                className="font-sans text-background/75 text-base leading-relaxed mb-10 animate-fade-up"
                style={{ animationDelay: "280ms" }}
              >
                A meal & weight tracker that won't shame you, gamify you, or sell
                your data. Log what you ate. See where you stand. Move on with
                your day.
              </p>

              <ul
                className="space-y-3 font-sans text-sm text-background/70 animate-fade-up"
                style={{ animationDelay: "440ms" }}
              >
                {[
                  "USDA-backed macros for thousands of foods",
                  "Recipes parsed & normalized to per-100g",
                  "Your data, on your hardware",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <span className="font-mono text-moss mt-[3px] text-xs">▸</span>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="eyebrow text-background/40">self-hosted · oci · v0.1.0-α</p>
        </div>
      </section>

      {/* Right — form */}
      <section className="bg-background flex flex-col">
        <div className="px-8 sm:px-14 pt-10 lg:hidden">
          <Brand subtitle="sign in" />
        </div>
        <div className="flex-1 grid place-items-center px-8 sm:px-14 py-12 lg:py-0">
          <div className="w-full max-w-sm">
            <p className="eyebrow mb-4">
              {prefilled ? "welcome — claim your invite" : "step 01 — magic link"}
            </p>
            <h2 className="font-display text-display tracking-tight mb-3">
              {prefilled ? (
                <>
                  Almost <span className="italic text-moss">in</span>.
                </>
              ) : (
                <>Tell us where to send the link.</>
              )}
            </h2>
            <p className="font-sans text-sm text-muted-foreground mb-10">
              {prefilled
                ? "We've filled in your email and invite code from the link. Tap Send to receive your one-tap sign-in."
                : "We'll email a one-tap sign-in. No password to remember, lose, or rotate."}
            </p>

            {state.kind === "sent" ? (
              <SentState email={state.email} />
            ) : (
              <form onSubmit={onSubmit} className="space-y-6">
                <Field
                  label="Email"
                  hint="The one your invite was issued to."
                  input={
                    <input
                      type="email"
                      required
                      autoFocus
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@somewhere.com"
                      className="w-full bg-transparent border-0 border-b border-border focus:border-moss focus:outline-none px-0 py-3 font-sans text-lg tracking-tight transition-colors"
                    />
                  }
                />
                <Field
                  label="Invite code"
                  hint="Required if this is your first sign-in. Otherwise leave blank."
                  input={
                    <input
                      type="text"
                      autoComplete="off"
                      value={invite}
                      onChange={(e) => setInvite(e.target.value.toUpperCase())}
                      placeholder="XXXX·XXXX"
                      maxLength={12}
                      className="w-full bg-transparent border-0 border-b border-border focus:border-moss focus:outline-none px-0 py-3 font-mono text-base tracking-wider transition-colors"
                    />
                  }
                />

                {state.kind === "error" && (
                  <p className="font-sans text-sm text-persimmon">
                    {state.msg}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={state.kind === "sending" || !email}
                  className="group relative w-full bg-foreground text-background py-4 rounded font-sans text-sm tracking-tight transition-all hover:bg-moss disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center justify-center gap-3">
                    {state.kind === "sending" ? "Sending…" : "Send sign-in link"}
                    <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
                  </span>
                </button>

                <p className="eyebrow text-center pt-2">
                  no password · no tracking · single-use link
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

function Field({
  label,
  hint,
  input,
}: {
  label: string
  hint?: string
  input: React.ReactNode
}) {
  return (
    <div>
      <label className="block eyebrow mb-2">{label}</label>
      {input}
      {hint && <p className="font-sans text-xs text-muted-foreground mt-2">{hint}</p>}
    </div>
  )
}

function SentState({ email }: { email: string }) {
  return (
    <div className="space-y-6">
      <div className="card-surface px-6 py-7">
        <p className="eyebrow mb-3">check your inbox</p>
        <p className="font-sans text-base text-foreground leading-relaxed">
          We sent a sign-in link to
        </p>
        <p
          className="font-display text-2xl italic text-moss tracking-tight mt-1 mb-4 break-words"
        >
          {email}
        </p>
        <p className="font-sans text-sm text-muted-foreground leading-relaxed">
          Click the link in the email to finish signing in. It expires in
          15&nbsp;minutes.
        </p>
      </div>
      <p className="eyebrow text-center">
        no email? check spam · or try again in a minute
      </p>
    </div>
  )
}
